/**
 * Anchor close-time analytics events at the message they cite (ENG-3195,
 * Mason 2026-09-01): the funnel should read in the flow of the conversation —
 * "what's my balance?" followed by its `balance_asked` pill — rather than as
 * a cluster after the last message, which is where the rows physically sit
 * (they are written when the conversation closes and take the next timeline
 * positions).
 *
 * Each pill moves to just after the NEAREST PRECEDING message whose
 * messageIndex matches its cited `metadata.messageIndex`, behind any pills
 * already anchored there (preserving emission order). Nearest-preceding is
 * what keeps this correct in the Contact Center's stitched multi-conversation
 * thread, where messageIndex restarts per conversation: a pill can only cite
 * a message from its own conversation, and that message is always the closest
 * match walking backwards. A pill with no resolvable anchor (unresolved
 * citation, filtered view) stays where it is. Runtime tool events carry no
 * citation and never move — they were written live, already in context.
 *
 * Structural type rather than ConversationMessage so the pure function (and
 * its spec) don't drag the whole @swivl-mono/types barrel through ts-jest.
 */
interface AnchorableMessage {
  messageIndex: number;
  type?: string;
  metadata?: unknown;
}

export function anchorAnalyticsEvents<T extends AnchorableMessage>(messages: T[]): T[] {
  const out: T[] = [];
  for (const msg of messages) {
    const meta = (msg.metadata ?? {}) as { messageIndex?: unknown };
    const cited = msg.type === "analytics_event" && typeof meta.messageIndex === "number" ? meta.messageIndex : null;
    if (cited == null) {
      out.push(msg);
      continue;
    }
    let anchor = -1;
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i].type !== "analytics_event" && out[i].messageIndex === cited) {
        anchor = i;
        break;
      }
    }
    if (anchor === -1) {
      out.push(msg);
      continue;
    }
    let insertAt = anchor + 1;
    while (insertAt < out.length && out[insertAt].type === "analytics_event") insertAt++;
    out.splice(insertAt, 0, msg);
  }
  return out;
}
